import { useId, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Modal, useConfirmDialog } from '@safetyhub/ui';
import { adminApi } from '../lib/api';
import { ModalCancelButton } from '../components/ModalCancelButton';

// ============================================
// 타입 정의
// ============================================

interface HazardType {
  id: string;
  code: string;
  name: string;
  description: string | null;
  parentId?: string | null;
  level: number;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
  _count: { children: number; industryMappings: number };
  children?: HazardType[];
}

interface HazardTypeDetail {
  id: string;
  code: string;
  name: string;
  description: string | null;
  parentId: string | null;
  level: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  parent: { id: string; code: string; name: string } | null;
  children: Array<{ id: string; code: string; name: string; isActive: boolean }>;
  industryMappings: Array<{
    id: string;
    industry: { id: string; code: string; name: string };
    priority: number;
    percentage: number | null;
  }>;
}

// ============================================
// 메인 컴포넌트
// ============================================

export default function HazardTypes() {
  const queryClient = useQueryClient();
  const [confirm, confirmDialog] = useConfirmDialog();
  const [viewMode, setViewMode] = useState<'tree' | 'list'>('tree');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editTarget, setEditTarget] = useState<HazardType | null>(null);
  const [detailTarget, setDetailTarget] = useState<string | null>(null);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  // 통계 조회
  const { data: statsData } = useQuery({
    queryKey: ['admin', 'hazard-types', 'stats'],
    queryFn: () => adminApi.getHazardTypeStats(),
  });

  // 트리 조회
  const { data: treeData, isLoading: treeLoading } = useQuery({
    queryKey: ['admin', 'hazard-types', 'tree'],
    queryFn: () => adminApi.getHazardTypesTree(),
    enabled: viewMode === 'tree',
  });

  // 리스트 조회
  const { data: listData, isLoading: listLoading } = useQuery({
    queryKey: ['admin', 'hazard-types', 'list'],
    queryFn: () => adminApi.getHazardTypes(),
    enabled: viewMode === 'list',
  });

  const toggleActiveMutation = useMutation({
    mutationFn: (id: string) => adminApi.toggleHazardTypeActive(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'hazard-types'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.deleteHazardType(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'hazard-types'] });
    },
  });

  // 트리·목록 두 보기가 같은 확인을 쓴다(종전엔 같은 confirm 문구를 두 자리에 손으로 적었다).
  const handleDelete = async (id: string) => {
    if (
      await confirm({
        title: '위험유형 삭제',
        body: '이 위험유형을 삭제하시겠습니까? 하위 항목이 있으면 삭제할 수 없습니다.',
        confirmLabel: '삭제',
        danger: true,
      })
    ) {
      deleteMutation.mutate(id);
    }
  };

  const toggleExpand = (id: string) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const expandAll = () => {
    if (treeData?.data) {
      const ids = new Set<string>();
      treeData.data.forEach((item) => {
        ids.add(item.id);
        item.children?.forEach((child) => ids.add(child.id));
      });
      setExpandedItems(ids);
    }
  };

  const collapseAll = () => {
    setExpandedItems(new Set());
  };

  const isLoading = viewMode === 'tree' ? treeLoading : listLoading;
  const stats = statsData?.data;

  return (
    <div>
      {/* 헤더 */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">위험유형 관리</h2>
        <p className="mt-1 text-sm text-gray-500">
          고용노동부 발생형태 코드 기반 위험유형을 관리합니다.
        </p>
      </div>

      {/* 통계 카드 */}
      {stats && (
        <div className="grid grid-cols-4 gap-4 mb-6">
          <Card radius="lg">
            <div className="text-sm text-gray-500">전체</div>
            <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
          </Card>
          <Card radius="lg">
            <div className="text-sm text-gray-500">활성</div>
            <div className="text-2xl font-bold text-green-600">{stats.active}</div>
          </Card>
          <Card radius="lg">
            <div className="text-sm text-gray-500">비활성</div>
            <div className="text-2xl font-bold text-gray-400">{stats.inactive}</div>
          </Card>
          <Card radius="lg">
            <div className="text-sm text-gray-500">레벨별</div>
            <div className="text-sm text-gray-700">
              {stats.byLevel.map((l) => `L${l.level}: ${l.count}`).join(', ')}
            </div>
          </Card>
        </div>
      )}

      {/* 도구 바 */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          {/* 보기 모드 전환 */}
          <div className="flex bg-gray-100 rounded p-1">
            <button
              onClick={() => setViewMode('tree')}
              className={`px-3 py-1 rounded text-sm ${
                viewMode === 'tree'
                  ? 'bg-white shadow text-gray-900'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              트리
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-1 rounded text-sm ${
                viewMode === 'list'
                  ? 'bg-white shadow text-gray-900'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              목록
            </button>
          </div>

          {viewMode === 'tree' && (
            <>
              <button
                onClick={expandAll}
                className="px-3 py-1 text-sm text-gray-600 hover:text-gray-900"
              >
                전체 펼치기
              </button>
              <button
                onClick={collapseAll}
                className="px-3 py-1 text-sm text-gray-600 hover:text-gray-900"
              >
                전체 접기
              </button>
            </>
          )}
        </div>

        <Button variant="primary" onClick={() => setShowAddModal(true)}>
          <span>+</span>
          <span>위험유형 추가</span>
        </Button>
      </div>

      {/* 콘텐츠 */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">로딩 중...</div>
        </div>
      ) : viewMode === 'tree' ? (
        <TreeView
          data={treeData?.data || []}
          expandedItems={expandedItems}
          onToggleExpand={toggleExpand}
          onEdit={setEditTarget}
          onDelete={handleDelete}
          onToggleActive={(id) => toggleActiveMutation.mutate(id)}
          onViewDetail={setDetailTarget}
        />
      ) : (
        <ListView
          data={listData?.data || []}
          onEdit={setEditTarget}
          onDelete={handleDelete}
          onToggleActive={(id) => toggleActiveMutation.mutate(id)}
          onViewDetail={setDetailTarget}
        />
      )}

      {/* 추가/수정 모달 */}
      {(showAddModal || editTarget) && (
        <HazardTypeModal
          hazardType={editTarget}
          parentOptions={treeData?.data || listData?.data || []}
          onClose={() => {
            setShowAddModal(false);
            setEditTarget(null);
          }}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'hazard-types'] });
            setShowAddModal(false);
            setEditTarget(null);
          }}
        />
      )}

      {/* 상세 모달 */}
      {detailTarget && (
        <HazardTypeDetailModal
          hazardTypeId={detailTarget}
          onClose={() => setDetailTarget(null)}
        />
      )}
      {confirmDialog}
    </div>
  );
}

// ============================================
// 트리 뷰
// ============================================

function TreeView({
  data,
  expandedItems,
  onToggleExpand,
  onEdit,
  onDelete,
  onToggleActive,
  onViewDetail,
}: {
  data: HazardType[];
  expandedItems: Set<string>;
  onToggleExpand: (id: string) => void;
  onEdit: (hazardType: HazardType) => void;
  onDelete: (id: string) => void;
  onToggleActive: (id: string) => void;
  onViewDetail: (id: string) => void;
}) {
  return (
    <Card padding="none" radius="lg">
      {data.length === 0 ? (
        <div className="p-8 text-center text-gray-500">등록된 위험유형이 없습니다.</div>
      ) : (
        <ul className="divide-y divide-gray-200">
          {data.map((item) => (
            <TreeItem
              key={item.id}
              item={item}
              depth={0}
              expandedItems={expandedItems}
              onToggleExpand={onToggleExpand}
              onEdit={onEdit}
              onDelete={onDelete}
              onToggleActive={onToggleActive}
              onViewDetail={onViewDetail}
            />
          ))}
        </ul>
      )}
    </Card>
  );
}

function TreeItem({
  item,
  depth,
  expandedItems,
  onToggleExpand,
  onEdit,
  onDelete,
  onToggleActive,
  onViewDetail,
}: {
  item: HazardType;
  depth: number;
  expandedItems: Set<string>;
  onToggleExpand: (id: string) => void;
  onEdit: (hazardType: HazardType) => void;
  onDelete: (id: string) => void;
  onToggleActive: (id: string) => void;
  onViewDetail: (id: string) => void;
}) {
  const isExpanded = expandedItems.has(item.id);
  const hasChildren = item.children && item.children.length > 0;

  // 위험 레벨에 따른 색상
  const getCodeColor = (code: string) => {
    const majorCode = code.substring(0, 2);
    const colors: Record<string, string> = {
      '01': 'bg-red-100 text-red-700',      // 떨어짐
      '02': 'bg-orange-100 text-orange-700', // 넘어짐
      '03': 'bg-yellow-100 text-yellow-700', // 깔림/뒤집힘
      '04': 'bg-blue-100 text-blue-700',     // 부딪힘
      '05': 'bg-purple-100 text-purple-700', // 물체에 맞음
      '06': 'bg-pink-100 text-pink-700',     // 무너짐
      '07': 'bg-indigo-100 text-indigo-700', // 끼임
      '08': 'bg-cyan-100 text-cyan-700',     // 절단/베임/찔림
      '09': 'bg-amber-100 text-amber-700',   // 감전
      '10': 'bg-rose-100 text-rose-700',     // 화재/폭발/파열
    };
    return colors[majorCode] || 'bg-gray-100 text-gray-700';
  };

  return (
    <li>
      <div
        className={`flex items-center gap-3 px-4 py-3 hover:bg-gray-50 ${
          !item.isActive ? 'opacity-50' : ''
        }`}
        style={{ paddingLeft: `${depth * 24 + 16}px` }}
      >
        {/* 확장 버튼 */}
        <button
          onClick={() => onToggleExpand(item.id)}
          className={`w-5 h-5 flex items-center justify-center text-gray-400 ${
            !hasChildren ? 'invisible' : ''
          }`}
        >
          <svg
            className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>

        {/* 클릭 가능한 영역 (코드 + 이름) */}
        <button
          onClick={() => onViewDetail(item.id)}
          className="flex items-center gap-3 flex-1 text-left hover:text-brand-600 transition-colors"
        >
          <span className={`font-mono text-sm px-2 py-0.5 rounded ${getCodeColor(item.code)}`}>
            {item.code}
          </span>
          <span className="font-medium text-gray-900">{item.name}</span>
        </button>

        {/* 매핑 수 */}
        {(item._count?.industryMappings ?? 0) > 0 && (
          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
            업종 {item._count?.industryMappings}개
          </span>
        )}

        {/* 상태 */}
        <button
          onClick={() => onToggleActive(item.id)}
          className={`px-2 py-0.5 rounded text-xs ${
            item.isActive
              ? 'bg-green-100 text-green-700 hover:bg-green-200'
              : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
          }`}
        >
          {item.isActive ? '활성' : '비활성'}
        </button>

        {/* 액션 */}
        <div className="flex items-center gap-1">
          <button
            onClick={() => onEdit(item)}
            className="p-1 text-gray-400 hover:text-gray-600"
            title="수정"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
              />
            </svg>
          </button>
          <button
            onClick={() => onDelete(item.id)}
            className="p-1 text-gray-400 hover:text-red-600"
            title="삭제"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* 하위 항목 */}
      {isExpanded && hasChildren && (
        <ul>
          {item.children!.map((child) => (
            <TreeItem
              key={child.id}
              item={child}
              depth={depth + 1}
              expandedItems={expandedItems}
              onToggleExpand={onToggleExpand}
              onEdit={onEdit}
              onDelete={onDelete}
              onToggleActive={onToggleActive}
              onViewDetail={onViewDetail}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

// ============================================
// 리스트 뷰
// ============================================

function ListView({
  data,
  onEdit,
  onDelete,
  onToggleActive,
  onViewDetail,
}: {
  data: HazardType[];
  onEdit: (hazardType: HazardType) => void;
  onDelete: (id: string) => void;
  onToggleActive: (id: string) => void;
  onViewDetail: (id: string) => void;
}) {
  return (
    <Card padding="none" radius="lg" className="overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              코드
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              위험유형명
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              레벨
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              하위/매핑
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              상태
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              관리
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {data.length === 0 ? (
            <tr>
              <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                등록된 위험유형이 없습니다.
              </td>
            </tr>
          ) : (
            data.map((item) => (
              <tr key={item.id} className={!item.isActive ? 'opacity-50' : ''}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <button
                    onClick={() => onViewDetail(item.id)}
                    className="font-mono text-sm bg-gray-100 px-2 py-0.5 rounded hover:bg-brand-100 hover:text-brand-700 transition-colors"
                  >
                    {item.code}
                  </button>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <button
                    onClick={() => onViewDetail(item.id)}
                    className="text-left hover:text-brand-600 transition-colors"
                  >
                    <span className="font-medium text-gray-900">{item.name}</span>
                    {item.description && (
                      <p className="text-sm text-gray-500 truncate max-w-xs">{item.description}</p>
                    )}
                  </button>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  L{item.level}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {item._count?.children ?? 0}개 / {item._count?.industryMappings ?? 0}개
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <button
                    onClick={() => onToggleActive(item.id)}
                    className={`px-2 py-1 rounded text-xs ${
                      item.isActive
                        ? 'bg-green-100 text-green-700 hover:bg-green-200'
                        : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                    }`}
                  >
                    {item.isActive ? '활성' : '비활성'}
                  </button>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                  <button
                    onClick={() => onEdit(item)}
                    className="text-gray-600 hover:text-gray-800 mr-3"
                  >
                    수정
                  </button>
                  <button
                    onClick={() => onDelete(item.id)}
                    className="text-red-600 hover:text-red-800"
                  >
                    삭제
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </Card>
  );
}

// ============================================
// 위험유형 추가/수정 모달
// ============================================

// export는 미저장 닫기 spec(`__tests__/formModalDirtyClose.test.tsx`)이 이 모달만 띄우기 위함이다.
export function HazardTypeModal({
  hazardType,
  parentOptions,
  onClose,
  onSuccess,
}: {
  hazardType: HazardType | null;
  parentOptions: HazardType[];
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [code, setCode] = useState(hazardType?.code || '');
  const [name, setName] = useState(hazardType?.name || '');
  const [description, setDescription] = useState(hazardType?.description || '');
  const [parentId, setParentId] = useState(hazardType?.parentId || '');
  const [isActive, setIsActive] = useState(hazardType?.isActive ?? true);
  const [error, setError] = useState('');
  const formId = useId();

  const isEdit = !!hazardType;

  // 부모 선택 옵션 평탄화
  const flattenOptions = (items: HazardType[], depth = 0): Array<{ id: string; label: string }> => {
    const result: Array<{ id: string; label: string }> = [];
    items.forEach((item) => {
      // 수정 시 자기 자신은 선택 불가
      if (isEdit && item.id === hazardType.id) return;
      result.push({
        id: item.id,
        label: `${'　'.repeat(depth)}${item.code} - ${item.name}`,
      });
      if (item.children) {
        result.push(...flattenOptions(item.children, depth + 1));
      }
    });
    return result;
  };

  const parentSelectOptions = flattenOptions(parentOptions);

  const mutation = useMutation({
    mutationFn: async () => {
      if (isEdit) {
        return await adminApi.updateHazardType(hazardType.id, {
          code,
          name,
          description: description || undefined,
          parentId: parentId || null,
          isActive,
        });
      } else {
        return await adminApi.createHazardType({
          code,
          name,
          description: description || undefined,
          parentId: parentId || undefined,
          isActive,
        });
      }
    },
    onSuccess: (result) => {
      if (result.success) {
        onSuccess();
      } else {
        setError(result.error?.message || '저장에 실패했습니다.');
      }
    },
    onError: () => {
      setError('저장 중 오류가 발생했습니다.');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!code || !name) {
      setError('코드와 이름은 필수 항목입니다.');
      return;
    }

    mutation.mutate();
  };

  // 미저장 닫기 보호 — 기준선은 열림 시점의 값(state 초기값과 같은 식). 생성이면 빈 폼이 기준이다.
  const [initial] = useState(() => ({
    code: hazardType?.code || '',
    name: hazardType?.name || '',
    description: hazardType?.description || '',
    parentId: hazardType?.parentId || '',
    isActive: hazardType?.isActive ?? true,
  }));
  const isDirty =
    code !== initial.code ||
    name !== initial.name ||
    description !== initial.description ||
    parentId !== initial.parentId ||
    isActive !== initial.isActive;

  return (
    // 부모가 열 때만 마운트하므로 open은 늘 true다. 제출 버튼이 base footer(폼 밖)에 있어 `form` 속성으로 묶는다.
    <Modal
      open
      onClose={onClose}
      title={isEdit ? '위험유형 수정' : '위험유형 추가'}
      size="md"
      isDirty={isDirty}
      footer={
        <>
          <ModalCancelButton onCancel={onClose} />
          <Button type="submit" form={formId} variant="primary" disabled={mutation.isPending}>
            {mutation.isPending ? '저장 중...' : '저장'}
          </Button>
        </>
      }
    >
        <form id={formId} onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded text-red-600 text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              위험유형 코드 *
            </label>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="예: 01, 0102"
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
            />
            <p className="mt-1 text-xs text-gray-500">고용노동부 발생형태 코드 형식을 따릅니다.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              위험유형명 *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="예: 떨어짐, 계단에서 떨어짐"
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              설명
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="위험유형에 대한 설명 (선택사항)"
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              상위 위험유형
            </label>
            <select
              value={parentId}
              onChange={(e) => setParentId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              <option value="">없음 (대분류)</option>
              {parentSelectOptions.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isActive"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="rounded border-gray-300"
            />
            <label htmlFor="isActive" className="text-sm text-gray-700">
              활성화
            </label>
          </div>
        </form>
    </Modal>
  );
}

// ============================================
// 위험유형 상세 모달
// ============================================

function HazardTypeDetailModal({
  hazardTypeId,
  onClose,
}: {
  hazardTypeId: string;
  onClose: () => void;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'hazard-types', hazardTypeId],
    queryFn: () => adminApi.getHazardType(hazardTypeId),
  });

  const hazardType = data?.data as HazardTypeDetail | undefined;

  return (
    // 종전 패널 스크롤(max-h-[80vh])은 base 본문 스크롤이 맡는다 — 헤더·닫기 줄이 고정된다.
    <Modal
      open
      onClose={onClose}
      title="위험유형 상세 정보"
      size="xl"
      footer={
        <button
          onClick={onClose}
          className="px-4 py-2 text-gray-600 hover:text-gray-800"
        >
          닫기
        </button>
      }
    >
        {isLoading ? (
          <div className="text-center py-8 text-gray-500">로딩 중...</div>
        ) : hazardType ? (
          <div className="space-y-6">
            {/* 기본 정보 */}
            <div>
              <h4 className="font-medium text-gray-900 mb-2">기본 정보</h4>
              <div className="bg-gray-50 rounded p-4 grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-gray-500">코드</div>
                  <div className="font-mono">{hazardType.code}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">위험유형명</div>
                  <div>{hazardType.name}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">레벨</div>
                  <div>L{hazardType.level}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">상태</div>
                  <div>
                    <span
                      className={`px-2 py-0.5 rounded text-xs ${
                        hazardType.isActive
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-500'
                      }`}
                    >
                      {hazardType.isActive ? '활성' : '비활성'}
                    </span>
                  </div>
                </div>
                {hazardType.description && (
                  <div className="col-span-2">
                    <div className="text-sm text-gray-500">설명</div>
                    <div>{hazardType.description}</div>
                  </div>
                )}
                {hazardType.parent && (
                  <div className="col-span-2">
                    <div className="text-sm text-gray-500">상위 위험유형</div>
                    <div>
                      <span className="font-mono text-sm bg-gray-100 px-2 py-0.5 rounded mr-2">
                        {hazardType.parent.code}
                      </span>
                      {hazardType.parent.name}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* 하위 위험유형 */}
            {hazardType.children.length > 0 && (
              <div>
                <h4 className="font-medium text-gray-900 mb-2">
                  하위 위험유형 ({hazardType.children.length}개)
                </h4>
                <div className="bg-gray-50 rounded p-4">
                  <ul className="space-y-2">
                    {hazardType.children.map((child) => (
                      <li key={child.id} className="flex items-center gap-2">
                        <span className="font-mono text-sm bg-gray-200 px-2 py-0.5 rounded">
                          {child.code}
                        </span>
                        <span className={!child.isActive ? 'text-gray-400' : ''}>
                          {child.name}
                        </span>
                        {!child.isActive && (
                          <span className="text-xs text-gray-400">(비활성)</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* 연결된 업종 */}
            {hazardType.industryMappings.length > 0 && (
              <div>
                <h4 className="font-medium text-gray-900 mb-2">
                  연결된 업종 ({hazardType.industryMappings.length}개)
                </h4>
                <div className="bg-gray-50 rounded overflow-hidden">
                  <table className="min-w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                          우선순위
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                          코드
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                          업종
                        </th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">
                          비중
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {hazardType.industryMappings
                        .sort((a, b) => a.priority - b.priority)
                        .map((mapping) => (
                          <tr key={mapping.id}>
                            <td className="px-4 py-2 text-sm">{mapping.priority}</td>
                            <td className="px-4 py-2">
                              <span className="font-mono text-sm bg-gray-200 px-2 py-0.5 rounded">
                                {mapping.industry?.code ?? '-'}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-sm">{mapping.industry?.name ?? '-'}</td>
                            <td className="px-4 py-2 text-sm text-right">
                              {mapping.percentage != null ? `${mapping.percentage}%` : '-'}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* 메타 정보 */}
            <div className="text-sm text-gray-400 pt-4 border-t">
              <p>생성: {new Date(hazardType.createdAt).toLocaleString('ko-KR')}</p>
              <p>수정: {new Date(hazardType.updatedAt).toLocaleString('ko-KR')}</p>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">위험유형 정보를 찾을 수 없습니다.</div>
        )}
    </Modal>
  );
}
